import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';
import { setSentryUser } from '@/lib/sentry';
import { toast } from 'sonner';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any; openedInNewTab?: boolean }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Server-side check: returns true if the currently authenticated user has
// app_users.active = true. Used to immediately log out deactivated users
// even if their auth session/refresh token is still valid.
async function checkUserActive(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('is_current_user_active');
    if (error) {
      console.warn('⚠️ is_current_user_active RPC error:', error.message);
      // Fail-open on RPC errors (network blip, etc.) — do NOT lock users out
      // because of transient infrastructure problems.
      return true;
    }
    return data === true;
  } catch (e) {
    console.warn('⚠️ is_current_user_active threw:', e);
    return true;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const enforceActive = async (sess: Session | null) => {
      if (!sess?.user) return true;
      const active = await checkUserActive();
      if (!active && !cancelled) {
        console.warn('🚫 Usuário inativo detectado — efetuando logout');
        toast.error('Sua conta está inativa. Entre em contato com o administrador.');
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
        return false;
      }
      return true;
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log('🔐 Auth event:', event, 'User:', newSession?.user?.email);

        // Update Sentry user context
        if (newSession?.user) {
          setSentryUser({
            id: newSession.user.id,
            email: newSession.user.email,
            display_name: newSession.user.user_metadata?.display_name,
          });
        } else {
          setSentryUser(null);
        }

        // Só atualiza se o usuário realmente mudou (evita re-render)
        setSession(prev => {
          if (prev?.user?.id === newSession?.user?.id) {
            return prev;
          }
          return newSession;
        });

        setUser(prev => {
          if (prev?.id === newSession?.user?.id) {
            return prev;
          }
          return newSession?.user ?? null;
        });

        setLoading(false);

        // On sign-in / token refresh, enforce that the user is still active.
        // Defer to avoid running inside the auth callback (Supabase guideline).
        if (
          newSession?.user &&
          (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED')
        ) {
          setTimeout(() => { void enforceActive(newSession); }, 0);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      console.log('🔍 Initial session check:', session?.user?.email);

      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Enforce on initial mount too (handles tabs reopened with cached session)
      if (session?.user) {
        await enforceActive(session);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const getAuthRedirectUrl = () => `${window.location.origin}/auth/callback`;

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    // Translate Supabase Auth "banned" message to a friendlier PT-BR message.
    // Banned = user marked inactive in app_users.
    if (error) {
      const msg = (error.message || '').toLowerCase();
      if (msg.includes('banned') || msg.includes('user is banned')) {
        return {
          error: {
            ...error,
            message: 'Sua conta está inativa. Entre em contato com o administrador.',
          },
        };
      }
    }

    return { error };
  };

  const signInWithGoogle = async () => {
    console.log('🚀 Iniciando Google OAuth...');

    const isEmbedded = window.self !== window.top;
    const authWindow = isEmbedded ? window.open('', '_blank') : null;

    if (authWindow) {
      authWindow.document.title = 'Entrando com Google...';
      authWindow.document.body.textContent = 'Redirecionando para o Google...';
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: getAuthRedirectUrl(),
        // O Google bloqueia OAuth dentro do iframe usado pelo Preview do Lovable.
        // Nesse ambiente obtemos a URL e abrimos o fluxo fora do iframe.
        skipBrowserRedirect: isEmbedded,
      },
    });

    if (error) {
      authWindow?.close();
      console.error('❌ Erro no Google OAuth:', error);
      return { error };
    }

    if (isEmbedded) {
      if (!authWindow || !data.url) {
        authWindow?.close();
        return {
          error: new Error(
            'Não foi possível abrir o login do Google. Permita pop-ups para este site e tente novamente.',
          ),
        };
      }

      // Preserve the opener until navigation: clearing it while the tab is
      // still about:blank removes this iframe's permission to navigate it.
      authWindow.location.href = data.url;
      return { error: null, openedInNewTab: true };
    }

    return { error: null };
  };
  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

let warnedOutsideAuthProvider = false;

const FALLBACK_AUTH_CONTEXT: AuthContextType = {
  user: null,
  session: null,
  loading: true,
  signIn: async () => ({ error: new Error('AuthProvider indisponível') }),
  signInWithGoogle: async () => ({ error: new Error('AuthProvider indisponível') }),
  signOut: async () => {},
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // Fail-closed: retorna defaults sem usuário em vez de lançar erro fatal.
    // Renders transitórios fora do provider (remonte após erro, chunk lazy,
    // StrictMode) não devem virar erro no Sentry — rotas protegidas seguem
    // redirecionando para /auth normalmente.
    if (!warnedOutsideAuthProvider) {
      warnedOutsideAuthProvider = true;
      console.warn('useAuth called outside AuthProvider - returning defaults');
    }
    return FALLBACK_AUTH_CONTEXT;
  }
  return context;
}

